const SENSITIVE_HEADER_RE =
  /(authorization|cookie|x-api-key|x-auth-token|x-csrf-token|x-session-token|x-restaurant-dining-session|x-shared-cart-access|x-order-tracking-(code|signature|phone)|x-trioty-edge-secret)/i;
const SENSITIVE_KEY_RE =
  /(token|password|secret|api[-_]?key|authorization|cookie|phone|email|address|coordinates?|latitude|longitude|buyer.?name|customer.?name|full.?name|signature|credential|proof|verifier|nonce|x-shared-cart-access|x-order-tracking-code|^launch$|^ticket$|^signed[-_]?request$|^query[-_]?string$)/i;
const FILTERED = "[Filtered]";
const SAFE_QUERY_KEYS = new Set(["page", "limit", "offset", "status", "sort", "locale"]);

export function scrubUrl(value: string): string {
  // Keep the route and harmless pagination/status metadata. All other query
  // values are untrusted: `q`, legacy aliases and signed-URL vendor fields may
  // carry PII or bearer credentials without a predictable key name.
  const hash = value.indexOf("#");
  const withoutHash = hash < 0 ? value : value.slice(0, hash);
  const question = withoutHash.indexOf("?");
  const path = question < 0 ? withoutHash : withoutHash.slice(0, question);
  const safePath = scrubUrlPath(path);
  if (question < 0) return safePath;
  const params = new URLSearchParams(withoutHash.slice(question + 1));
  for (const key of [...params.keys()]) {
    const values = params.getAll(key);
    params.delete(key);
    for (const val of values) params.append(key,
      SAFE_QUERY_KEYS.has(key.toLowerCase()) && /^[a-zA-Z0-9_-]{1,40}$/.test(val) ? val : FILTERED);
  }
  return `${safePath}?${params.toString()}`;
}

function decodePathSegment(value: string): string {
  // Decode aliases before classifying them; do not decode slashes into route
  // boundaries (an encoded slash can belong to one bearer-token segment).
  for (let i = 0; i < 3; i += 1) {
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch { break; }
  }
  return value;
}

function scrubUrlPath(path: string): string {
  const withoutCredentials = path.replace(/(https?:\/\/)[^/@\s]+:[^/@\s]+@/gi, "$1[Filtered]@");
  const segments = withoutCredentials.split("/");
  const decoded = segments.map(decodePathSegment);
  return segments.map((segment, index) => {
    const previous = decoded[index - 1]?.toLowerCase();
    const invitation = previous === "invite" || previous === "store-invite";
    const sharedCart = previous === "carts" && decoded.slice(0, index).some(part => part.toLowerCase() === "public");
    if (segment && (invitation || sharedCart)) return FILTERED;
    const safe = decoded[index]!
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, FILTERED)
      .replace(/(?:\+\d(?:[\s()-]*\d){7,14}|\b(?:00)?(?:20|01)\d(?:[\s()-]*\d){7,12}\b)/g, FILTERED);
    return safe === decoded[index] ? segment : safe;
  }).join("/");
}

function scrubText(value: string): string {
  return value
    .replace(/(?:https?|trioty-seller):\/\/[^\s<>"']+/gi, (url) => scrubUrl(url))
    .replace(/(^|[\s(])\/[^\s<>"')]+/g, (path, prefix: string) => prefix + scrubUrl(path.slice(prefix.length)))
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, FILTERED)
    .replace(/(?:\+\d(?:[\s()-]*\d){7,14}|\b(?:00)?(?:20|01)\d(?:[\s()-]*\d){7,12}\b)/g, FILTERED);
}

export function scrubHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!headers) return headers;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    out[k] = SENSITIVE_HEADER_RE.test(k) || SENSITIVE_KEY_RE.test(k)
      ? FILTERED
      : /^(referer|referrer|location)$/i.test(k) ? scrubUrl(v) : scrubText(v);
  }
  return out;
}

export function scrubObject(value: unknown): unknown {
  return walk(value, new WeakSet());
}

type PrivacyContext = "person" | "location" | undefined;
function walk(value: unknown, seen: WeakSet<object>, context?: PrivacyContext): unknown {
  if (typeof value === "string") return scrubText(value);
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  seen.add(value);
  const out: Record<string, unknown> = {};
  if (Array.isArray(value)) {
    const result = value.map((v) => walk(v, seen, context));
    seen.delete(value);
    return result;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const childContext: PrivacyContext = /^(?:customer|buyer|user|person|contact|recipient|seller|member|staff|owner|admin)s?$/i.test(k)
      ? "person" : /^(?:location|geolocation|position|geo)$/i.test(k)
        ? "location" : /^(?:product|service|trace)$/i.test(k) ? undefined : context;
    const contextualPii = (context === "person" && /^(?:name|first[-_]?name|last[-_]?name|username|display[-_]?name)$/i.test(k)) ||
      (context === "location" && /^(?:lat|lng|lon)$/i.test(k));
    out[k] = contextualPii || SENSITIVE_KEY_RE.test(k) || /^(sig|hmac|session|key)$/i.test(k)
      ? FILTERED
      : typeof v === "string" && /(?:url(?:\.full)?|uri|referer|referrer|from|to|target)$/i.test(k)
        ? scrubUrl(v) : walk(v, seen, childContext);
  }
  seen.delete(value);
  return out;
}

function scrubRequestData(value: unknown): unknown {
  if (typeof value !== "string") return scrubObject(value);
  try { return scrubObject(JSON.parse(value)); }
  catch { return FILTERED; } // opaque/form/raw bodies cannot be safely classified
}

export function scrubEvent<T extends { request?: { headers?: Record<string, string>; cookies?: unknown; data?: unknown } }>(event: T): T {
  const safe = scrubObject(event) as T;
  if (safe.request) {
    safe.request.headers = scrubHeaders(event.request?.headers);
    delete safe.request.cookies;
    if (event.request && "data" in event.request) safe.request.data = scrubRequestData(event.request.data);
  }
  return safe;
}

export function scrubBreadcrumb<T>(breadcrumb: T): T {
  return scrubObject(breadcrumb) as T;
}

export function parseRate(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 1) return fallback;
  return n;
}
